import apiService from '../util/request.js';
import SMP_CONFIG from '@/util/smp_config';
import TrackingApiService from '@/services/TrackingApiService';

/** module that includes api functions that deal with bookings/offers
 * @module bookingApiService */

/**
 * List all tos/agreements for application's context key
 * @returns {AxiosPromise<any>}
 */
function getAgreements() {
  let params = {
    context: SMP_CONFIG.contextConfig.key,
    max: 100,
    offset: 0
  };
  return apiService.get('/smp/api/agreements', { params });
}

/**
 * create new booking object
 * @param {String} payload.timeslotId id of a selected timeslot
 * @param {String} payload.offerKey key of selected offer
 * @param {String} payload.comment comment
 * @param {String} payload.status status of a booking
 * @param {String} payload.emailAddress email of a customer
 * @param {String} payload.givenname first name of a customer
 * @param {String} payload.surname last name of a customer
 * @param {String} payload.phone phone of a customer
 * @param {String[]} payload.selectedServiceIds ids of selected vehicle services
 * @param {Number} payload.mileage mileage of a vehicle
 * @returns {AxiosPromise<any>}
 */
const createBooking = payload => {
  return apiService.post('/smp/api/bookedoffers2', payload).then((res) => {
    TrackingApiService.trackWithFg({
      section: 'BookingConfirmed',
      search_id: res.data.searchConfigKey,
      offer_id: res.data.offerKey,
      value_4: res.data.key,
      booked_offer_key: res.data.bookedOfferNumber
    });

    return res;
  })
}

/**
 * create new booking object from quote
 * @param {String} payload.timeslotId id of a selected timeslot
 * @param {String} payload.comment comment
 * @param {String[]} payload.selectedServiceIds ids of selected vehicle services
 * @param {String} payload.user.email email of a customer
 * @param {String} payload.user.firstName first name of a customer
 * @param {String} payload.user.lastName last name of a customer
 * @param {String} payload.user.phone phone of a customer
 * @param {String} payload.vehicle.licensePlate license plate of a vehicle
 * @param {Number} payload.vehicle.mileage mileage of a vehicle
 * @param {String} key key field from a quote object
 * @returns {AxiosPromise<any>}
 */
const createBookingFromQuote = (payload, key) => {
  return apiService.put(`/smp/api/bookedoffers2/${key}/book`, payload)
};

/**
 * fetch booking details by booking key
 * @param {String} key key of a booking
 * @returns {AxiosPromise<any>}
 */
const getBookingDetails = key => {
  let bookingDetail;
  return apiService.get(`/smp/api/bookedoffers2/${key}`).then((res) => {
    bookingDetail = res.data;
    return getLatestBookingVersion(key);
  })
    .then((res) => {
      bookingDetail.searchConfig = res.searchConfig;
      bookingDetail.latest = res.latest;
      return bookingDetail;
    });
};

/**
 * fetch latest version of booking details by booking key
 * @param {String} key key of a booking
 * @param {String[]} params.selectedServiceIds ids of selected vehicle services
 * @returns {AxiosPromise<any>}
 */
const getLatestBookingVersion = (key, params) => {
  let bookingDetail = {};
  params = params || {};
  return apiService.get(`/smp/api/bookedoffers2/${key}/versions/latest`, { params }).then((res) => {
    bookingDetail.searchConfig = res.data.searchConfig;
    bookingDetail.latest = res.data;
    return bookingDetail;
  })
};

/**
 * fetch available timeslots for an offer
 * @param {String} offerKey offer key
 * @returns {AxiosPromise<any>}
 */
const getTimeSlots = (offerKey) => {
  return apiService.get(`/smp/api/offer2/offers/${offerKey}/timeslot`);
}

/**
 * fetch available timeslots for an offer
 * @param {String} bookedOfferKey offer key
 * @returns {AxiosPromise<any>}
 */
const getOfferedOfferTimeslots = bookedOfferKey => {
  return apiService.get(`/smp/api/bookedoffers2/${bookedOfferKey}/timeslot`);
}

/**
 * fetch available timeslots for an offer
 * @param {String} offerKey offer key
 * @returns {AxiosPromise<any>}
 */
const getTimeslots = offerKey => {
  return apiService.get(`/smp/api/offer2/offers/${offerKey}/timeslot`);
}

/**
 * fetch consumer ratings for a location
 * @param {Number} locationId id of a location
 * @returns {AxiosPromise<any>}
 */
const getLocationVoting = locationId => {
  const params = { locationId };
  return apiService.get('/smp/api/bookedoffers2/votings', { params });
}

/**
 * update booking status
 * @param {String} bookedOfferKey key of an offer
 * @param {String} statusInfo new status
 * @returns {AxiosPromise<any>}
 */
const updateStatus = (bookedOfferKey, statusInfo) => {
  return apiService.put(`/smp/api/bookedoffers2/${bookedOfferKey}/status`, null, { params: statusInfo });
}

/**
 * postpone a booking
 * @param {String} bookedOfferKey key of an offer
 * @param {String} params.timeslotId new timeslot id
 * @returns {AxiosPromise<any>}
 */
const postponeBooking = (bookedOfferKey, params) => {
  return apiService.put(`/smp/api/bookedoffers2/${bookedOfferKey}/postpone`, null, { params });
}

/** add a rating to a location
 * @param {String} bookedOfferKey key of an offer
 * @param {Boolean} ratingInfo.takenPlace has the visit to the location happened or not
 * @param {Number} ratingInfo.bookedOfferVotingTypeMap.DETAIL_FRIENDLINESS[0] friendliness rating
 * @param {Number} ratingInfo.bookedOfferVotingTypeMap.DETAIL_FRIENDLINESS[0] quality rating
 * @param {Number} ratingInfo.bookedOfferVotingTypeMap.DETAIL_FRIENDLINESS[0] reliability rating
 * @param {Number} ratingInfo.bookedOfferVotingTypeMap.DETAIL_FRIENDLINESS[0] overall satisfaction rating
 * @param {String} ratingInfo.comment comment
 * @param {null | 'others'} ratingInfo.notTakenPlace reason for the visit not happening
 * @param {Number} ratingInfo.servicePrice price paid
 * @param {Number} ratingInfo.locationId id of a location (from the booking/offer)
 * @param {String} ratingInfo.vehicle vehicle prop the booking/offer
 * @param {String[]} ratingInfo.services ids of services (from the booking/offer)
 * @returns {AxiosPromise<any>}
 */
const createBookingVoting = (bookedOfferKey, ratingInfo) => {
  ratingInfo.bookedOfferKey = bookedOfferKey;
  return apiService.post('/smp/api/bookedoffers2/votings', ratingInfo);
}

/** fetch location rating by bookedOfferKey
 * @param {String} bookedOfferKey key of an offer/booking
 * @returns {AxiosPromise<any>}
 */
const getBookingVoting = bookedOfferKey => {
  const params = { bookedOfferKey };
  return apiService.get('/smp/api/bookedoffers2/votings', { params });
}

/** fetch services
 * @param {String} params.fza fza of a vehicle
 * @param {String} params.hst hst of a vehicle
 * @param {String} params.ht ht of a vehicle
 * @param {String} params.ut ut of a vehicle
 * @param {String[]} params.externalIds externalIds of services
 * @param {Number} params.max maximum returned items
 * @returns {AxiosPromise<any>}
 */
function getServices(params) {
  return apiService.get('/smp/api/taxonomy/services', { params });
}

export default {
  getAgreements,
  createBooking,
  createBookingFromQuote,
  getBookingDetails,
  getTimeSlots,
  getOfferedOfferTimeslots,
  getLocationVoting,
  updateStatus,
  getLatestBookingVersion,
  getTimeslots,
  postponeBooking,
  createBookingVoting,
  getBookingVoting,
  getServices
}
